/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { generateAriaLabel } from '#utils/UNSAFE_visUtils/accUtils';
import { BundleType } from '../../resources/nls/bundle';
import type { GanttProps, GanttRowData, GanttTaskData } from '../gantt.types';
import { GanttRowLayoutObj, GanttTaskLayoutObj, getNormalizedStartEnd } from './dataLayoutUtils';

/**
 * Gets the default aria label for the task.
 * @param taskObj The task layout object.
 * @param rowData The rows data.
 * @param dateFormatter The date formatter.
 * @param translations The translations bundle.
 * @returns The default aria label.
 */
const getDefaultTaskAccessibleLabel = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>(
  taskObj: GanttTaskLayoutObj<K2>,
  rowData: GanttProps<K2, D1, D2>['rows'],
  // TODO: take valueFormats instead of dateFormatter when we support the API
  dateFormatter: (date: string) => string,
  translations: BundleType
) => {
  const row = rowData[taskObj.rowIndex];
  const task = row.tasks[taskObj.index];

  const { start, end } = getNormalizedStartEnd(task);
  const isMilestone = start === end;

  const desc = [];
  if (row.label) {
    const rowDesc = translations.vis_labelAndValue({
      LABEL: translations.gantt_labelRow(),
      VALUE: row.label
    });
    desc.push(rowDesc);
  }
  if (!isMilestone) {
    const startDesc = translations.vis_labelAndValue({
      LABEL: translations.gantt_labelStart(),
      VALUE: dateFormatter(start)
    });
    const endDesc = translations.vis_labelAndValue({
      LABEL: translations.gantt_labelEnd(),
      VALUE: dateFormatter(end)
    });
    desc.push(startDesc);
    desc.push(endDesc);
  } else {
    const dateDesc = translations.vis_labelAndValue({
      LABEL: translations.gantt_labelDate(),
      VALUE: dateFormatter(start)
    });
    desc.push(dateDesc);
  }
  if (task.label) {
    const labelDesc = translations.vis_labelAndValue({
      LABEL: translations.gantt_labelLabel(),
      VALUE: task.label
    });
    desc.push(labelDesc);
  }
  return desc.join('; ');
};

/**
 * Gets the aria label for the task.
 * @param taskObj The task layout object.
 * @param rowData The rows data.
 * @param dateFormatter The date formatter.
 * @param translations The translations bundle.
 * @returns The aria label for the task.
 */
const getTaskAriaLabel = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>(
  taskObj: GanttTaskLayoutObj<K2>,
  rowData: GanttProps<K2, D1, D2>['rows'],
  dateFormatter: (date: string) => string,
  translations: BundleType
) => {
  const data = rowData[taskObj.rowIndex].tasks[taskObj.index];
  const accessibleLabel =
    data.accessibleLabel !== undefined
      ? data.accessibleLabel
      : getDefaultTaskAccessibleLabel<K1, K2, D1, D2>(
          taskObj,
          rowData,
          dateFormatter,
          translations
        );

  return generateAriaLabel(translations, accessibleLabel, { isSelected: taskObj.isSelected });
};

/**
 * Gets the aria label for the row label
 * @param rowObj The row layout object.
 * @param rowData The rows data.
 * @returns The aria label for the row label.
 */
const getRowLabelAriaLabel = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>(
  rowObj: GanttRowLayoutObj<K1, K2>,
  rowData: GanttProps<K2, D1, D2>['rows']
) => {
  // TODO: There will be states when we support tree data
  const data = rowData[rowObj.index];
  const accessibleLabel = data.accessibleLabel || data.label;
  return accessibleLabel;
};

export { getTaskAriaLabel, getRowLabelAriaLabel };
